import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import UriHelper, { useSentinelQuery } from '../../../../lib/uri-helper';
import Analytics from '../../../../lib/user-analytics';
import {
    actionSentinelBeginBoxSelect,
    actionSentinelChangeOpacity,
    actionSentinelClearFeatures,
    actionSentinelEndBoxSelect,
    actionSentinelFetchFeatures,
    actionSentinelResetSelectedAOI,
    actionSentinelResetSelectedFeature,
    actionSentinelSelectAOI,
    actionSentinelSelectFeature,
} from '../../../../store/Map/Sentinel/actions';
import { SentinelMetadata } from '../../../../store/Map/Sentinel/model';
import {
    selectSentinelError,
    selectSentinelFeatures,
    selectSentinelFeaturesLoading,
    selectSentinelIsBoxSelectActive,
    selectSentinelSelectedAOI,
    selectSentinelSelectedFeature,
} from '../../../../store/Map/Sentinel/selectors';
import { SideDrawerMode } from '../../../../store/SideDrawer/model';
import SatelliteLoading from '../satellite-loading';
import SentinelFilter, { DateRange, last30Days } from './sentinel-filter';
import SentinelItem from './sentinel-item';
import SentinelLowResolutionNotice from './sentinel-low-resolution-notice';
import { actionFlyTo } from '../../../../store/App/actions';
import Constants from '../../../../constants';
import { addSentinelParamsToUrl, getDateFromShareLink, getLayerFromShareLink } from '../../../../lib/sentinel-service';
import DrawerHint from '../../drawer-hint';
import {
    BackArrow,
    BackText,
    Button,
    ChevronIcon,
    Container,
    Content,
    Header,
    Logo,
    ShowMoreButton,
    ShowMoreContainer,
    TeaserIcon,
    TeaserText,
    EmptyTeaserTitle,
    LogoContainer,
} from '../satellite-drawer-styles';

const moment = require('moment');

const SentinelDrawer = () => {
    const bbox = useSelector(selectSentinelSelectedAOI);
    const selectedSentinelFeature = useSelector(selectSentinelSelectedFeature);
    const isBoxSelectActive = useSelector(selectSentinelIsBoxSelectActive);
    const sentinelFeatures = useSelector(selectSentinelFeatures);
    const sentinelFeaturesLoading = useSelector(selectSentinelFeaturesLoading);
    const sentinelError = useSelector(selectSentinelError);

    const dispatch = useDispatch();
    const beginBoxSelect = () => dispatch(actionSentinelBeginBoxSelect());
    const endBoxSelect = () => dispatch(actionSentinelEndBoxSelect());
    const resetSelectedAOI = () => dispatch(actionSentinelResetSelectedAOI());
    const resetSelectedSentinelFeature = () => dispatch(actionSentinelResetSelectedFeature());
    const clearSentinelFeatures = () => dispatch(actionSentinelClearFeatures());

    const [layer, setLayer] = useState<SentinelMetadata>(Constants.OVERLAY_DATA.SENTINEL.TRUE_COLOR);
    const [dateRange, setDateRange] = useState<DateRange>(last30Days);
    const [showMoreLoading, setShowMoreLoading] = useState(false);
    const [closeDraw, setCloseDraw] = useState<boolean>(false);

    const sentinelShareFeature = useSentinelQuery();

    const invalidateSentinelResults = () => {
        clearSentinelFeatures();
        resetSelectedAOI();
        resetSelectedSentinelFeature();
        endBoxSelect();
        setLayer(Constants.OVERLAY_DATA.SENTINEL.TRUE_COLOR);
    };

    useEffect(() => {
        if (sentinelShareFeature) {
            dispatch(actionFlyTo(sentinelShareFeature.bbox, undefined, true));
            dispatch(actionSentinelSelectAOI(sentinelShareFeature.bbox));
            if (UriHelper.tryGetParam('time')) {
                setDateRange(getDateFromShareLink(sentinelShareFeature.date));
            }
            if (UriHelper.tryGetParam('layerKey')) {
                setLayer(getLayerFromShareLink(sentinelShareFeature.layer));
            }
            dispatch(actionSentinelSelectFeature(sentinelShareFeature));
        }

        return () => {
            invalidateSentinelResults();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedSentinelFeature) {
            addSentinelParamsToUrl(selectedSentinelFeature);
        }
    }, [selectedSentinelFeature]);

    useEffect(() => {
        //reset opacity
        dispatch(actionSentinelChangeOpacity(undefined));
        if (bbox && layer) {
            dispatch(actionSentinelFetchFeatures(bbox, dateRange.startDate, dateRange.endDate, layer));
        }
    }, [bbox, layer, dateRange, dispatch]);

    const handleShowMore = () => {
        Analytics.Event('Satellite - Sentinel', 'Clicked show more');
        setShowMoreLoading(true);
        const newStartDate = moment(dateRange.startDate).subtract(1, 'months').toDate();
        setDateRange({ ...dateRange, startDate: newStartDate });
    };

    useEffect(() => {
        if (sentinelFeatures) {
            setShowMoreLoading(false);
        }
    }, [sentinelFeatures]);

    const isLowResolution = sentinelFeatures && sentinelFeatures[0].resolution !== '10m';

    const reset = () => {
        if (sentinelFeatures) {
            invalidateSentinelResults();
        } else {
            clearSentinelFeatures();
            resetSelectedAOI();
            UriHelper.navigateToDrawer(SideDrawerMode.SATELLITE);
            endBoxSelect();
            Analytics.Event('Satellite - Sentinel', 'Clicked back arrow');
        }
    };

    return (
        <Container closeDraw={closeDraw}>
            <Header closeDraw={closeDraw}>
                <BackArrow data-testid="sentinel-back-button" onClick={reset} />
                <BackText onClick={reset}>Back</BackText>
                <ChevronIcon
                    onClick={() => setCloseDraw(!closeDraw)}
                    className={closeDraw ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
                />
                <LogoContainer>
                    <Logo src="/assets/floating-drawer-satellite-icons/satellite-sentinel-logo.png" />
                </LogoContainer>
            </Header>

            {!sentinelFeatures && !sentinelFeaturesLoading && sentinelError ? (
                <Content>
                    <EmptyTeaserTitle />
                    <TeaserIcon src="/assets/floating-drawer-icons/map-teaser-icon.svg" />
                    <Button
                        id="draw-aoi-button"
                        onClick={() => {
                            invalidateSentinelResults();
                            Analytics.Event('Satellite - Sentinel', 'Cleared Sentinel Feature');
                        }}
                    >
                        RESET AREA OF INTEREST
                    </Button>
                </Content>
            ) : null}

            {!sentinelFeaturesLoading && !sentinelFeatures && !sentinelError ? (
                <Content>
                    <EmptyTeaserTitle />
                    <TeaserIcon src="/assets/floating-drawer-icons/map-teaser-icon.svg" />
                    <TeaserText>Click the button and drag an Area of Interest.</TeaserText>
                    <TeaserText>Let's see what we can find!</TeaserText>
                    <Button
                        id="draw-aoi-button"
                        disabled={isBoxSelectActive}
                        onClick={() => {
                            beginBoxSelect();
                            Analytics.Event('Satellite - Sentinel', 'Clicked draw area of interest');
                        }}
                    >
                        DRAW AOI
                    </Button>
                </Content>
            ) : null}

            {sentinelFeaturesLoading && !showMoreLoading && !sentinelError ? (
                <Content>
                    <SatelliteLoading />
                </Content>
            ) : null}

            {sentinelFeatures && !sentinelError && (!sentinelFeaturesLoading || showMoreLoading) ? (
                <React.Fragment>
                    <SentinelFilter
                        onSelectLayer={(layer) => setLayer(layer)}
                        onSelectDateRange={(dateRange) => setDateRange(dateRange)}
                        layer={layer}
                        dateRange={dateRange}
                    />
                    <Content>
                        {isLowResolution ? <SentinelLowResolutionNotice /> : null}

                        {sentinelFeatures.map((t, index) => (
                            <SentinelItem key={index} feature={t} selectedFeature={selectedSentinelFeature} />
                        ))}

                        <ShowMoreContainer>
                            {showMoreLoading ? (
                                <ShowMoreButton>
                                    <Spinner color="light" />
                                </ShowMoreButton>
                            ) : (
                                <ShowMoreButton
                                    onClick={() => {
                                        handleShowMore();
                                        Analytics.Event('Satellite - Sentinel', 'Clicked show more');
                                    }}
                                >
                                    SHOW MORE
                                </ShowMoreButton>
                            )}
                        </ShowMoreContainer>
                    </Content>
                </React.Fragment>
            ) : null}
            {sentinelError ? <DrawerHint error>{sentinelError.message || `${sentinelError}`}</DrawerHint> : null}
        </Container>
    );
};

export default SentinelDrawer;
